import { createContext, FC, useMemo, ReactNode, useContext, useRef } from "react";
import { useAuth } from "src/hooks/use-auth";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { paths } from "src/paths";
import {
  AxiosError,
  // AxiosRequestConfig
} from "axios";

const axios = require("axios").default;

interface EstateAppApiProviderProps {
  children: ReactNode;
}

const EstateAppApiContext = createContext({
  estateAppApiInstance: axios,
});

// interface CustomAxiosRequestConfig extends AxiosRequestConfig {
//   _retry?: boolean;
// }

export const EstateAppApiProvider: FC<EstateAppApiProviderProps> = (props) => {
  const { children } = props;
  const authStuff = useAuth();
  // Read auth through a ref so the axios instance is built once. Rebuilding it on every auth
  // change gave every hook using it a new identity, which re-fired their fetch effects; with a
  // 401 -> signOut -> auth change cycle that became an infinite request loop.
  const authRef = useRef(authStuff);
  authRef.current = authStuff;
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const estateAppApiInstance = useMemo(() => {
    const instance = axios.create({
      baseURL: process.env.REACT_APP_SNUG_API_URL,
    });

    instance.interceptors.request.use((config: any) => {
      if (localStorage.getItem("s_at")) {
        config.headers.Authorization = "Bearer " + localStorage.getItem("s_at");
      }
      config.headers["X-User-Domain"] = window.location.host.replace(
        "https://",
        ""
      );
      return config;
    });

    instance.interceptors.response.use(
      (response: any) => response,
      async (error: AxiosError) => {
        //
        // Need to figure this out, we wnt to refresh token if it is expired, but the resulting behavior of the code below for multiple api refresh
        // was for the token to be refreshed but still be directed to login page from some refresh errors. For now we're just signing out, but ideal UX
        // would sipmly be to refresh token and continue the UX without blip.
        //
        // const originalRequest = error.config as CustomAxiosRequestConfig;
        //
        // if (error.response?.status === 401 && !originalRequest._retry) {
        //   originalRequest._retry = true;
        //   try {
        //     await authStuff.refreshToken(); // Refresh the token
        //     instance.defaults.headers.common["Authorization"] =
        //       "Bearer " + localStorage.getItem("s_at");
        //     return instance(originalRequest);
        //   } catch (err) {
        //     // Handle error, show message or redirect to login page
        //   }
        // }
        // Only sign out when this request actually carried a session. A 401 on a request made
        // without a token means we're already signed out; signing out again changes auth
        // state for nothing and can re-trigger the request.
        const hadSession = Boolean((error.config as any)?.headers?.Authorization);
        // The client's firm requires MFA and this session hasn't passed it. Not signed in, not
        // a permissions problem: send them to set it up (or to sign in again with their code).
        const data: any = error.response?.data;
        if (error.response?.status === 403 && data?.code === "mfa_required") {
          // Many requests fail at once; only the first one (while still in the app) routes.
          if (!window.location.pathname.startsWith("/auth/")) {
            const returnTo = window.location.pathname + window.location.search;
            if (data.enrolled) {
              // Their session predates MFA; signing in again asks for the code.
              navigateRef.current(
                paths.auth.jwt.entryStart + "?" + new URLSearchParams({ ep: "login", returnTo }).toString(),
                { replace: true }
              );
              authRef.current.signOut();
              toast("For your security, please sign in again.", { id: "mfa_required" });
            } else {
              navigateRef.current(
                paths.auth.jwt.mfaSetup + "?" + new URLSearchParams({ returnTo }).toString(),
                { replace: true }
              );
            }
          }
          return Promise.reject(error);
        }
        if ((error.response?.status === 401 || error.response?.status === 403) && hadSession) {
          authRef.current.signOut();
        }
        return Promise.reject(error);
      }
    );

    return instance;
  }, []);

  return (
    <EstateAppApiContext.Provider
      value={{
        estateAppApiInstance,
      }}
    >
      {children}
    </EstateAppApiContext.Provider>
  );
};

export const useEstateAppApiContext = () => useContext(EstateAppApiContext);
